import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline'
import { Box, LinearProgress } from '@mui/material'
import { Button } from '../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../constants/colors'

// Type declarations for dynamically loaded external libraries
interface LottieAnimation {
  addEventListener: (event: string, callback: () => void) => void
  goToAndStop: (frame: number, isFrame: boolean) => void
  goToAndPlay: (frame: number, isFrame: boolean) => void
  destroy: () => void
  totalFrames: number
}

interface LottieLib {
  loadAnimation: (config: {
    container: HTMLElement
    renderer: string
    loop: boolean
    autoplay: boolean
    animationData: unknown
  }) => LottieAnimation
}

interface WindowWithLottie extends Window {
  lottie?: LottieLib
  webkitAudioContext?: typeof AudioContext
}

// Lottie Script Loader Component
const LottieScriptLoader: React.FC = () => {
  useEffect(() => {
    // Check if Lottie is already loaded
    if (typeof window !== 'undefined' && (window as WindowWithLottie).lottie) {
      return
    }

    // Load Lottie script if not already loaded
    const script = document.createElement('script')
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js'
    script.async = true
    script.onload = () => {}
    script.onerror = () => {}
    document.head.appendChild(script)

    return () => {
      // Cleanup script if component unmounts
      const existingScript = document.querySelector('script[src*="lottie.min.js"]')
      if (existingScript) {
        document.head.removeChild(existingScript)
      }
    }
  }, [])

  return null // This component doesn't render anything
}

/**
 * Voice Animation with Audio Synchronization Page (Optimized with Global Lottie API)
 * Based on: https://lottiefiles.com/blog/working-with-lottie-animations/how-to-animate-lottie-in-response-to-audio
 */
export const VoiceAnimationWithAudioOptimized: React.FC = () => {
  const navigate = useNavigate()

  // State for UI and animation control
  const [voiceAnimationData, setVoiceAnimationData] = useState<object | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [audioLevel, setAudioLevel] = useState(0) // Smoothed dB level (UI only)
  const [animationProgress, setAnimationProgress] = useState(0) // 0-1 range (UI only)
  const [targetProgress, setTargetProgress] = useState(0) // Target progress for smooth interpolation (UI only)

  // Refs for continuous smoothing (bypasses React state for performance)
  const smoothedDbRef = useRef<number>(0) // Continuous exponential smoothing at 60 FPS
  const lastUIUpdateTime = useRef<number>(0) // Throttle UI updates to 10 FPS

  // Refs for audio processing and Lottie
  const audioRef = useRef<HTMLAudioElement>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const [lottieInstance, setLottieInstance] = useState<LottieAnimation | null>(null)
  const lottieContainerRef = useRef<HTMLDivElement>(null) // Ref for the DOM element Lottie attaches to

  // Debug flag for development
  const DEBUG = true // Set to false for production

  // Audio processing parameters (optimized for smoothness)
  const minDb = -50 // Minimum decibel level (adjusted for better range)
  const maxDb = -10 // Maximum decibel level (adjusted for better range)
  const alpha = 0.95 // Higher smoothing factor for smoother animation (was 0.8)
  const preGain = 2.0 // Pre-gain for sensitivity control (increased)

  // Load Lottie animation data
  useEffect(() => {
    const loadAnimationData = async () => {
      try {
        const response = await fetch('/assets/voice-animation.json')
        if (!response.ok) {
          throw new Error('Failed to load animation data')
        }
        const data = await response.json()

        setVoiceAnimationData(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load animation')
      } finally {
        setLoading(false)
      }
    }

    loadAnimationData()
  }, [])

  // Mount Lottie Animation to DOM when data and ref are ready
  useEffect(() => {
    if (
      voiceAnimationData &&
      lottieContainerRef.current &&
      typeof (window as WindowWithLottie).lottie !== 'undefined'
    ) {
      const animation = (window as WindowWithLottie).lottie!.loadAnimation({
        container: lottieContainerRef.current,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        animationData: voiceAnimationData
      })

      // The key is to capture the instance AFTER the component has mounted
      animation.addEventListener('DOMLoaded', () => {
        setLottieInstance(animation)
      })

      // Ensure the initial frame is 0
      animation.goToAndStop(0, true)

      return () => {
        animation.destroy()
        setLottieInstance(null)
      }
    }

    // Return undefined if conditions are not met
    return undefined
  }, [voiceAnimationData])

  // Initialize audio context
  useEffect(() => {
    if (audioRef.current) {
      initializeAudioContext()
    }

    return () => {
      stopAudioAnalysis()
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [])

  // Initialize audio context and analyser
  const initializeAudioContext = async () => {
    if (!audioRef.current) return

    try {
      // Only create audio context if it doesn't exist
      if (!audioContextRef.current) {
        // webkit prefix for older browsers - using any is acceptable for browser API compatibility
        audioContextRef.current = new (window.AudioContext || (window as WindowWithLottie).webkitAudioContext)()
      }

      // Only create analyser if it doesn't exist
      if (!analyserRef.current) {
        analyserRef.current = audioContextRef.current.createAnalyser()
        analyserRef.current.fftSize = 1024 // Use power of 2 for better performance
        analyserRef.current.smoothingTimeConstant = 0.8

        // Connect audio element to analyser (only once)
        const source = audioContextRef.current.createMediaElementSource(audioRef.current)
        source.connect(analyserRef.current)
        analyserRef.current.connect(audioContextRef.current.destination)
      }
    } catch (err) {
      setError('Failed to initialize audio processing')
    }
  }

  // Calculate RMS (Root Mean Square) from audio buffer
  const calculateRMS = (buffer: Uint8Array): number => {
    let sum = 0
    for (let i = 0; i < buffer.length; i++) {
      const normalized = (buffer[i] - 128) / 128
      sum += normalized * normalized
    }
    return Math.sqrt(sum / buffer.length)
  }

  // Convert RMS to decibels
  const rmsToDecibels = (rms: number): number => {
    if (rms < 0.0001) return -100
    return 20 * Math.log10(rms)
  }

  // Convert decibels to animation progress (0-1)
  const getProgress = (inputDb: number): number => {
    const clampedDb = Math.max(Math.min(inputDb, maxDb), minDb)
    const normalizedDb = (clampedDb - minDb) / (maxDb - minDb)
    const progress = Math.min(Math.max(normalizedDb, 0), 1)
    return progress
  }

  // Sample audio and update animation (optimized with ref-based smoothing and throttled UI)
  const sampleAndAnimate = () => {
    if (!analyserRef.current || !lottieInstance) {
      // Stop the loop if dependencies are missing (CRITICAL FIX)
      stopAudioAnalysis()
      return
    }

    const currentTime = Date.now()

    // Use time-domain data for amplitude calculation
    const bufferLength = analyserRef.current.fftSize
    const dataArray = new Uint8Array(bufferLength)
    analyserRef.current.getByteTimeDomainData(dataArray)

    // Calculate RMS (correct for time-domain data)
    const rms = calculateRMS(dataArray)
    const db = rmsToDecibels(rms)

    // *** PERFORMANCE OPTIMIZATION: Ref-based exponential smoothing at 60 FPS ***
    // This runs continuously without React state updates for maximum smoothness
    const previousSmoothedDb = smoothedDbRef.current
    const currentSmoothedDb = previousSmoothedDb * alpha + (1 - alpha) * db
    smoothedDbRef.current = currentSmoothedDb

    // Apply pre-gain
    const gainedDb = preGain * currentSmoothedDb
    const newTargetProgress = getProgress(gainedDb)

    // *** PERFORMANCE OPTIMIZATION: Direct Lottie update at 60 FPS ***
    // Update Lottie animation directly without React state for smooth visual animation
    try {
      const totalFrames = lottieInstance.totalFrames
      const frameNumber = newTargetProgress * totalFrames

      // This is the direct Lottie API call - runs at 60 FPS for smooth animation
      lottieInstance.goToAndStop(frameNumber, true)

      if (DEBUG) {
        // Reduce log frequency to avoid spam
        if (Math.random() < 0.02) {
          // Only log 2% of the time
        }
      }
    } catch (err) {}

    // *** PERFORMANCE OPTIMIZATION: Throttled UI updates (10 FPS) ***
    // Only update React state for UI display every 100ms to avoid excessive re-renders
    const timeSinceLastUIUpdate = currentTime - lastUIUpdateTime.current
    if (timeSinceLastUIUpdate >= 100) {
      // 10 FPS for UI updates
      setAudioLevel(gainedDb)
      setTargetProgress(newTargetProgress)
      setAnimationProgress(newTargetProgress)
      lastUIUpdateTime.current = currentTime
    }

    // Continue sampling at 60 FPS
    animationFrameRef.current = requestAnimationFrame(sampleAndAnimate)
  }

  // Start audio analysis
  const startAudioAnalysis = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
    sampleAndAnimate()
  }

  // Stop audio analysis
  const stopAudioAnalysis = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }

  // Handle user interaction to start audio context
  const handleUserInteraction = async () => {
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume()
      } catch (err) {}
    }
  }

  // Handle play/pause
  const handlePlayPause = async () => {
    if (!audioRef.current || !voiceAnimationData) return

    await handleUserInteraction()

    if (isPlaying) {
      audioRef.current.pause()
      stopAudioAnalysis()
      setIsPlaying(false)
    } else {
      // CRITICAL FIX: Ensure Lottie instance is ready
      if (!lottieInstance) {
        setError('Lottie animation failed to load. Please wait or reload.')
        return
      }

      try {
        audioRef.current.currentTime = 0 // Ensure playback starts from the beginning
        lottieInstance.goToAndStop(0, true) // Reset animation state
        await audioRef.current.play()
        startAudioAnalysis()
        setIsPlaying(true)
      } catch (err) {
        setError('Failed to play audio. Ensure a file is loaded.')
      }
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <>
      <LottieScriptLoader />
      <Box sx={{ padding: '24px', backgroundColor: INTERAX_COLORS.offBlack, minHeight: '100vh' }}>
        {/* Header with back button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
          <Button variant="secondary" onClick={handleBack} icon={<ArrowLeftIcon className="w-4 h-4" />} />
          <Box className="text-headline-1" sx={{ color: INTERAX_COLORS.white }}>
            Voice Animation with Audio Sync (Optimized)
          </Box>
        </Box>

        <Box className="text-body-1" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '32px' }}>
          Synchronized voice animation that responds to audio levels in real-time using global Lottie API
        </Box>

        {/* Audio Element */}
        <audio
          ref={audioRef}
          src="/assets/063847_french-female-voice-44607.mp3"
          preload="auto"
          onEnded={() => {
            setIsPlaying(false)
            stopAudioAnalysis()
            setAnimationProgress(0)
            setAudioLevel(0)
          }}
        />

        {/* Animation Container */}
        <Box
          ref={lottieContainerRef}
          sx={{
            padding: '32px',
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            borderRadius: '12px',
            border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            marginBottom: '32px'
          }}
        >
          {loading && (
            <Box className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
              Loading animation data...
            </Box>
          )}

          {error && (
            <Box className="text-body-2" sx={{ color: INTERAX_COLORS.redError }}>
              Error: {error}
            </Box>
          )}

          {!loading && !error && voiceAnimationData && (
            <>
              <Box className="text-body-2" sx={{ color: INTERAX_COLORS.white, marginBottom: '16px' }}>
                Voice Animation with Audio Synchronization (Global Lottie API)
              </Box>
              {/* This is the container for the Lottie animation - it will be populated by the global Lottie API */}
              <Box
                sx={{
                  width: '300px',
                  height: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {!lottieInstance && (
                  <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
                    Initializing Lottie animation...
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Controls */}
        <Box
          sx={{
            padding: '24px',
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            borderRadius: '12px',
            border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
            marginBottom: '24px'
          }}
        >
          <Box className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '16px' }}>
            Audio Controls & Debug
          </Box>

          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
            <Button
              variant="primary"
              onClick={handlePlayPause}
              disabled={Boolean(!voiceAnimationData || loading || error || !lottieInstance)}
              icon={isPlaying ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
            >
              {isPlaying ? 'Pause' : 'Play'} Audio
            </Button>

            <Box
              sx={{
                padding: '8px 12px',
                borderRadius: '8px',
                backgroundColor: lottieInstance ? INTERAX_COLORS.heritageBlue : INTERAX_COLORS.redError
              }}
            >
              <Box className="text-body-3" sx={{ color: INTERAX_COLORS.white }}>
                Status: {lottieInstance ? 'Lottie Ready (Instance Captured)' : 'Waiting for Lottie...'}
              </Box>
            </Box>
          </Box>

          {/* Audio Level Indicator */}
          <Box sx={{ marginBottom: '16px' }}>
            <Box className="text-body-2" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Raw Audio Level (Gained): {audioLevel.toFixed(2)} dB (Target range: {minDb} dB to {maxDb} dB)
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, audioLevel + 100))}
              sx={{
                height: '8px',
                borderRadius: '4px',
                backgroundColor: INTERAX_COLORS.thunderGrey,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: INTERAX_COLORS.heritageBlue
                }
              }}
            />
          </Box>

          {/* Animation Progress Indicator */}
          <Box>
            <Box className="text-body-2" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Animation Progress: {(animationProgress * 100).toFixed(1)}%
              <span style={{ color: INTERAX_COLORS.thunderGrey, marginLeft: '8px' }}>
                (Target: {(targetProgress * 100).toFixed(1)}%)
              </span>
            </Box>
            <LinearProgress
              variant="determinate"
              value={animationProgress * 100}
              sx={{
                height: '8px',
                borderRadius: '4px',
                backgroundColor: INTERAX_COLORS.thunderGrey,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: INTERAX_COLORS.sun
                }
              }}
            />
            {/* Target Progress Indicator (subtle overlay) */}
            <Box
              sx={{
                height: '2px',
                width: `${targetProgress * 100}%`,
                backgroundColor: INTERAX_COLORS.heritageBlue,
                borderRadius: '1px',
                marginTop: '2px',
                opacity: 0.6
              }}
            />
          </Box>
        </Box>

        {/* Technical Info */}
        <Box
          sx={{
            padding: '24px',
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            borderRadius: '12px',
            border: `1px solid ${INTERAX_COLORS.heritageBlue}`
          }}
        >
          <Box className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '16px' }}>
            Performance Optimizations
          </Box>
          <Box className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '8px' }}>
            <strong>Ref-Based Smoothing:</strong> Exponential smoothing now uses `useRef` to store the previous smoothed
            value, running at 60 FPS without React state updates for maximum accuracy.
          </Box>
          <Box className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '8px' }}>
            <strong>Throttled UI Updates:</strong> React state updates (`setAudioLevel`, `setAnimationProgress`) are
            throttled to 10 FPS (every 100ms) to prevent excessive re-renders while maintaining smooth visual feedback.
          </Box>
          <Box className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '8px' }}>
            <strong>Direct Lottie Control:</strong> Lottie animation updates (`goToAndStop`) run at 60 FPS in the RAF
            loop, ensuring smooth visual animation independent of React's render cycle.
          </Box>
          <Box className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
            <strong>Global Lottie API:</strong> Uses `window.lottie.loadAnimation()` with `DOMLoaded` event for reliable
            instance capture and direct DOM mounting.
          </Box>
        </Box>
      </Box>
    </>
  )
}
